import {
  AlertTriangle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Info,
  LayoutDashboard,
  Monitor,
  RefreshCw,
} from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import type { ApiError } from '../../api/envelope';
import type { DispatchMetric } from '../../api/types';
import { ErrorNote } from '../../components/ErrorNote';
import { exact, money } from '../analytics/analyticsFormat';
import type { MetricGroup } from './arrange';
import { type CardFace, figureFit } from './cardFace';
import { cardSubtitle, cardTitle, parseDayInput, stepLabel } from './cards';
import { ColumnChart, KpiSpark, TrendChart } from './charts/SeriesChart';
import { DeltaChip } from './FaceBody';
import { type Bucket, type CivilDay, PRESETS, type ResolvedFrame, dayString } from './frames';
import { PhoneSheet } from './PhoneSheet';
import { LIVE_MAX_DAYS } from './prefs';
import { PULL_THRESHOLD, usePullToRefresh } from './usePullToRefresh';
import './dashboardPhone.css';

/**
 * The operations dashboard on a phone — a layout of its own, and view-only.
 *
 * Its own screen rather than the desktop grid squeezed: a phone is read with a
 * thumb, one hand, in a car park between two pickups. So the figures come two
 * to a row in cards big enough to tap, every chart gets the full width, the
 * window is a sheet under the thumb instead of a strip of eleven buttons, a
 * chart is read by tapping rather than hovering, and a pull from the top
 * re-reads the page — the gesture the dispatch dashboard's phone shell uses
 * for every one of its own pages.
 *
 * VIEW-ONLY, by decision: on a phone nobody adds, edits, arranges or deletes a
 * metric. There is no New metric, no Arrange, no pencil and no drag, and the
 * host's shortcuts are left to the host's own phone navigation. That is said
 * once, quietly, at the foot of the page — the reader is told where editing
 * lives rather than left wondering whether their role lost it. The server's
 * `dashboard_edit` gate is unchanged: this is a layout that offers nothing to
 * edit, not a permission.
 *
 * It owns no data (the `<page>_mobile_screen` contract the dispatch
 * dashboard's phone screens follow): every read, the window and the card faces
 * belong to DashboardView, which hands them down. What a card SAYS comes from
 * the same `cardFace` the desktop grid draws, so a phone and a desk can differ
 * in layout and never in a figure.
 */

export interface PhoneListState {
  loading: boolean;
  error: ApiError | null;
  retry: () => void;
}

const GROUP_LABELS: Record<'recent' | 'calendar' | 'long', string> = {
  recent: 'Recent',
  calendar: 'Calendar',
  long: 'Longer',
};

const BUCKET_LABELS: Record<Bucket, string> = {
  day: 'Daily',
  week: 'Weekly',
  month: 'Monthly',
  quarter: 'Quarterly',
  year: 'Yearly',
};

/** A single-series chart's figure for the window, printed in the card's corner. */
const totalOf = (face: Extract<CardFace, { kind: 'chart' }>): string | null => {
  const { chart } = face;
  // Several series are several counts of overlapping sets — New and Cancelled
  // jobs — and their sum is a number that is true of nothing.
  if (chart.series.length !== 1 || chart.kind === null) return null;
  const total = chart.totals[0] ?? 0;
  return chart.kind === 'currency' ? money(total) : exact(total);
};

/* ---- the header ----------------------------------------------------------- */

function PhoneHeader({
  switcher,
  greeting,
  rangeLabel,
  days,
  windowLabel,
  canStepForward,
  live,
  onStep,
  onOpenWindow,
}: {
  switcher: ReactNode;
  greeting: string;
  rangeLabel: string;
  days: number;
  windowLabel: string;
  canStepForward: boolean;
  live: boolean;
  onStep: (direction: -1 | 1) => void;
  onOpenWindow: () => void;
}) {
  return (
    <header className="dbp-head">
      {switcher}
      <div className="dbp-hello">
        <h1>{greeting}</h1>
        {/* The window pill sits with the greeting, where the thumb already is,
            and says what the page is showing without being opened. */}
        <button
          type="button"
          className="dbp-window"
          aria-haspopup="dialog"
          aria-label={`Time window: ${windowLabel}. Change it`}
          onClick={onOpenWindow}
        >
          <span>{windowLabel}</span>
          <ChevronDown size={15} aria-hidden="true" />
        </button>
      </div>
      <div className="dbp-range">
        <button
          type="button"
          className="dbp-step"
          aria-label={stepLabel(-1, days)}
          onClick={() => onStep(-1)}
        >
          <ChevronLeft size={18} />
        </button>
        <span className="dbp-range-label">{rangeLabel}</span>
        <button
          type="button"
          className="dbp-step"
          aria-label={stepLabel(1, days)}
          // Forward stops at today: a window running into the future reads as
          // a collapse in every figure on the page.
          disabled={!canStepForward}
          onClick={() => onStep(1)}
        >
          <ChevronRight size={18} />
        </button>
        {live ? (
          <span className="dbp-live" title="Re-reads every 90 seconds while this screen is open">
            <i aria-hidden="true" />
            Live
          </span>
        ) : null}
      </div>
    </header>
  );
}

/* ---- the window sheet ----------------------------------------------------- */

function WindowSheet({
  frame,
  frameKey,
  customActive,
  buckets,
  livePref,
  liveRuns,
  onPick,
  onCustom,
  onBucket,
  onLive,
  onClose,
}: {
  frame: ResolvedFrame;
  frameKey: string;
  customActive: boolean;
  buckets: Bucket[];
  livePref: boolean;
  liveRuns: boolean;
  onPick: (key: string) => void;
  onCustom: (range: { from: CivilDay; to: CivilDay }) => void;
  onBucket: (bucket: Bucket) => void;
  onLive: (on: boolean) => void;
  onClose: () => void;
}) {
  const [customOpen, setCustomOpen] = useState(customActive);
  const [from, setFrom] = useState<CivilDay>(frame.from);
  const [to, setTo] = useState<CivilDay>(frame.to);
  const customRef = useRef<HTMLDivElement | null>(null);

  // The two fields open at the foot of the sheet, under the keyboard's reach
  // and often under the fold: bring them up to where they can be filled in.
  useEffect(() => {
    if (customOpen) customRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [customOpen]);

  const groups = (['recent', 'calendar', 'long'] as const).map((group) => ({
    group,
    presets: PRESETS.filter((preset) => preset.group === group),
  }));

  return (
    <PhoneSheet title="Time window" onClose={onClose}>
      {/* Two to a row, so every window is in view at once — a list of eleven
          needs scrolling on most phones, and the one scrolled past is the one
          nobody finds. */}
      {groups.map(({ group, presets }) => (
        <div key={group} className="dbp-sheet-group">
          <p className="dbp-option-group">{GROUP_LABELS[group]}</p>
          <div className="dbp-presets" role="group" aria-label={GROUP_LABELS[group]}>
            {presets.map((preset) => {
              const on = frameKey === preset.key && !customActive;
              return (
                <button
                  key={preset.key}
                  type="button"
                  className={on ? 'dbp-preset on' : 'dbp-preset'}
                  aria-pressed={on}
                  onClick={() => {
                    onPick(preset.key);
                    onClose();
                  }}
                >
                  {preset.label}
                </button>
              );
            })}
          </div>
        </div>
      ))}

      <div className="dbp-sheet-group">
        <p className="dbp-option-group">Your own</p>
        <button
          type="button"
          className={customActive ? 'dbp-preset wide on' : 'dbp-preset wide'}
          aria-expanded={customOpen}
          onClick={() => setCustomOpen((open) => !open)}
        >
          <span>Custom range</span>
          <ChevronDown size={18} aria-hidden="true" className={customOpen ? 'dbp-turned' : undefined} />
        </button>
        {customOpen ? (
          <div ref={customRef} className="dbp-custom">
            <label>
              From
              <input
                type="date"
                defaultValue={dayString(frame.from)}
                onChange={(event) => {
                  const day = parseDayInput(event.target.value);
                  if (day) setFrom(day);
                }}
              />
            </label>
            <label>
              To
              <input
                type="date"
                defaultValue={dayString(frame.to)}
                onChange={(event) => {
                  const day = parseDayInput(event.target.value);
                  if (day) setTo(day);
                }}
              />
            </label>
            <button
              type="button"
              className="btn btn-primary dbp-apply"
              onClick={() => {
                onCustom({ from, to });
                onClose();
              }}
            >
              Show this range
            </button>
          </div>
        ) : null}
      </div>

      {/* Only the groupings this window can honestly be drawn at (frames.ts). */}
      {buckets.length > 1 ? (
        <div className="dbp-sheet-group">
          <p className="dbp-option-group">Charts by</p>
          <div className="dbp-chips" role="group" aria-label="Charts by">
            {buckets.map((bucket) => (
              <button
                key={bucket}
                type="button"
                className={frame.bucket === bucket ? 'dbp-chip on' : 'dbp-chip'}
                aria-pressed={frame.bucket === bucket}
                onClick={() => onBucket(bucket)}
              >
                {BUCKET_LABELS[bucket]}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {/* The reader's own setting, in their own browser: it changes nothing on
          the shared dashboard, and on a phone's data plan it is worth having. */}
      <label className="dbp-switch-row">
        <span>
          <strong>Keep up to date</strong>
          <small>
            {liveRuns
              ? 'Re-reads every 90 seconds while this screen is open'
              : `Only on windows of ${LIVE_MAX_DAYS} days or fewer`}
          </small>
        </span>
        <input
          type="checkbox"
          role="switch"
          className="dbp-switch"
          checked={livePref}
          onChange={(event) => onLive(event.target.checked)}
        />
      </label>
    </PhoneSheet>
  );
}

/* ---- the cards ------------------------------------------------------------ */

/** What a card that has no figure to show says instead — never a zero. */
function FaceNotice({ face }: { face: Extract<CardFace, { kind: 'failed' | 'refused' | 'unavailable' }> }) {
  if (face.kind === 'unavailable') {
    return (
      <p className="dbp-notice">
        <Info size={14} aria-hidden="true" />
        <span>{face.message}</span>
      </p>
    );
  }
  return (
    <p className="dbp-notice warn">
      <AlertTriangle size={14} aria-hidden="true" />
      <span>
        {face.kind === 'refused' ? `Couldn’t load: ${face.message}` : face.error.message || 'Couldn’t load this one.'}
      </span>
    </p>
  );
}

function KpiTile({
  metric,
  face,
  onOpen,
}: {
  metric: DispatchMetric;
  face: CardFace;
  onOpen: (metric: DispatchMetric) => void;
}) {
  const title = cardTitle(metric);

  if (face.kind === 'loading') {
    return (
      <div className="dbp-kpi" aria-busy="true" aria-label={`${title}, loading`}>
        <span className="dbp-kpi-title">{title}</span>
        <span className="dbp-shimmer figure" />
        <span className="dbp-shimmer line" />
      </div>
    );
  }
  if (face.kind !== 'kpi') {
    // Nothing behind it to open: a failure has no rows, and a reason is the
    // whole answer.
    return (
      <div className="dbp-kpi quiet">
        <span className="dbp-kpi-title">{title}</span>
        {face.kind === 'chart' ? null : <FaceNotice face={face} />}
      </div>
    );
  }

  const change = face.foot.kind === 'delta' ? `, ${face.foot.delta.label}` : '';
  return (
    <button
      type="button"
      className="dbp-kpi"
      aria-label={`${title}: ${face.figure}${change}. Show what it is made of`}
      onClick={() => onOpen(metric)}
    >
      <span className="dbp-kpi-title">{title}</span>
      <span className="dbp-figure-fit">
        <span className="dbp-figure" style={figureFit(face.figure)}>
          {face.figure}
        </span>
      </span>
      <span className="dbp-kpi-foot">
        {face.foot.kind === 'delta' ? (
          <DeltaChip delta={face.foot.delta} size={12} />
        ) : face.foot.text ? (
          <span className="dbp-kpi-note">{face.foot.text}</span>
        ) : null}
        {face.caption ? <span className="dbp-kpi-caption">{face.caption}</span> : null}
      </span>
      {face.spark ? (
        <span className="dbp-spark">
          <KpiSpark data={face.spark} />
        </span>
      ) : null}
    </button>
  );
}

function ChartCard({
  metric,
  face,
  onOpen,
}: {
  metric: DispatchMetric;
  face: CardFace;
  onOpen: (metric: DispatchMetric) => void;
}) {
  const title = cardTitle(metric);
  const subtitle = cardSubtitle(metric.subCategory.filter(Boolean), title, metric.category);
  const total = face.kind === 'chart' ? totalOf(face) : null;
  const openable = face.kind === 'chart';

  const heading = (
    <>
      <span className="dbp-chart-name">
        <strong>{title}</strong>
        <span>{subtitle}</span>
      </span>
      {total ? <span className="dbp-chart-total">{total}</span> : null}
      {openable ? <ChevronRight size={18} className="dbp-chart-go" aria-hidden="true" /> : null}
    </>
  );

  return (
    <article className="dbp-chart">
      {openable ? (
        <button
          type="button"
          className="dbp-chart-head"
          aria-label={`${title}. Show what it is made of`}
          onClick={() => onOpen(metric)}
        >
          {heading}
        </button>
      ) : (
        <div className="dbp-chart-head">{heading}</div>
      )}
      <div className="dbp-chart-body">
        {face.kind === 'loading' ? (
          <span className="dbp-shimmer chart" aria-label="Loading" />
        ) : face.kind === 'chart' ? (
          face.line ? (
            <TrendChart data={face.chart} height={190} highlight={face.highlight} focusNote={face.note} touch />
          ) : (
            <ColumnChart data={face.chart} height={190} highlight={face.highlight} focusNote={face.note} touch />
          )
        ) : face.kind === 'kpi' ? null : (
          <FaceNotice face={face} />
        )}
      </div>
    </article>
  );
}

/* ---- the states around the cards ------------------------------------------ */

function PhoneSkeleton() {
  return (
    <div className="dbp-skeleton" aria-busy="true" aria-label="Loading the dashboard">
      <div className="dbp-kpis">
        {[0, 1, 2, 3].map((index) => (
          <div key={index} className="dbp-kpi">
            <span className="dbp-shimmer title" />
            <span className="dbp-shimmer figure" />
            <span className="dbp-shimmer line" />
          </div>
        ))}
      </div>
      <div className="dbp-chart">
        <span className="dbp-shimmer title wide" />
        <span className="dbp-shimmer chart" />
      </div>
    </div>
  );
}

function PhoneEmpty() {
  return (
    <div className="dbp-empty">
      <span className="dbp-empty-mark" aria-hidden="true">
        <LayoutDashboard size={26} />
      </span>
      <h2>No metrics yet</h2>
      <p>
        Metrics are chosen on the dashboard on a computer. Once somebody adds them, they appear here for
        everyone.
      </p>
    </div>
  );
}

function PullIndicator({ distance, armed, refreshing }: { distance: number; armed: boolean; refreshing: boolean }) {
  if (!distance && !refreshing) return null;
  // Slides in from the page's top edge with the finger, and turns as it goes,
  // so how far there is left to pull is visible before letting go.
  const offset = Math.min(distance, PULL_THRESHOLD + 30) - 40;
  return (
    <div className="dbp-pull-track" aria-hidden="true">
      <div
        className={refreshing ? 'dbp-pull refreshing' : armed ? 'dbp-pull armed' : 'dbp-pull'}
        style={{ transform: `translate(-50%, ${offset}px)` }}
      >
        <RefreshCw size={18} style={refreshing ? undefined : { transform: `rotate(${distance * 3}deg)` }} />
      </div>
    </div>
  );
}

/* ---- the screen ----------------------------------------------------------- */

export function DashboardPhone({
  embedded,
  switcher = null,
  greeting,
  frame,
  frameKey,
  customActive,
  rangeLabel,
  canStepForward,
  buckets,
  live,
  livePref,
  list,
  groups,
  faceOf,
  refreshing,
  onStep,
  onPickFrame,
  onCustom,
  onBucket,
  onLive,
  onRefresh,
  onOpen,
}: {
  embedded: boolean;
  /** The section's Operations/Analytics switch, above the greeting. */
  switcher?: ReactNode;
  greeting: string;
  frame: ResolvedFrame;
  frameKey: string;
  customActive: boolean;
  rangeLabel: string;
  canStepForward: boolean;
  buckets: Bucket[];
  /** Re-reading on a timer right now — asked for, and the window is short enough. */
  live: boolean;
  /** Asked for, whatever the window: what the switch shows. */
  livePref: boolean;
  list: PhoneListState;
  groups: MetricGroup[];
  faceOf: (metric: DispatchMetric) => CardFace;
  refreshing: boolean;
  onStep: (direction: -1 | 1) => void;
  onPickFrame: (key: string) => void;
  onCustom: (range: { from: CivilDay; to: CivilDay }) => void;
  onBucket: (bucket: Bucket) => void;
  onLive: (on: boolean) => void;
  onRefresh: () => void;
  onOpen: (metric: DispatchMetric) => void;
}) {
  const root = useRef<HTMLDivElement | null>(null);
  const [windowOpen, setWindowOpen] = useState(false);
  const pull = usePullToRefresh(root, { onRefresh, refreshing, enabled: !windowOpen });

  const windowLabel = customActive
    ? 'Custom range'
    : (PRESETS.find((preset) => preset.key === frameKey)?.label ?? frame.label);
  const hasKpis = useMemo(() => groups.some((group) => group.kind === 'kpi'), [groups]);

  let content: ReactNode;
  if (list.loading) {
    content = <PhoneSkeleton />;
  } else if (list.error) {
    content = (
      <div className="dbp-failed">
        <ErrorNote error={list.error} />
        <button type="button" className="btn btn-secondary dbp-retry" onClick={list.retry}>
          <RefreshCw size={15} aria-hidden="true" />
          Try again
        </button>
      </div>
    );
  } else if (!groups.length) {
    content = <PhoneEmpty />;
  } else {
    content = groups.map((group) => {
      const label = group.kind === 'chart' ? (hasKpis ? 'Charts' : null) : group.label;
      return (
        <section key={group.key} className={`dbp-band ${group.kind}`} aria-label={label ?? 'Headline figures'}>
          {label ? <h2 className="dbp-band-title">{label}</h2> : null}
          {group.kind === 'kpi' ? (
            <div className="dbp-kpis">
              {group.metrics.map((metric) => (
                <KpiTile key={metric.id} metric={metric} face={faceOf(metric)} onOpen={onOpen} />
              ))}
            </div>
          ) : (
            <div className="dbp-charts">
              {group.metrics.map((metric) => (
                <ChartCard key={metric.id} metric={metric} face={faceOf(metric)} onOpen={onOpen} />
              ))}
            </div>
          )}
        </section>
      );
    });
  }

  return (
    <div ref={root} className={embedded ? 'dbp embedded' : 'dbp'}>
      <PullIndicator distance={pull.distance} armed={pull.armed} refreshing={pull.phase === 'refreshing'} />
      <PhoneHeader
        switcher={switcher}
        greeting={greeting}
        rangeLabel={rangeLabel}
        days={frame.days}
        windowLabel={windowLabel}
        canStepForward={canStepForward}
        live={live}
        onStep={onStep}
        onOpenWindow={() => setWindowOpen(true)}
      />
      <main className="dbp-body">{content}</main>
      {/* Said once, where a reader looking for the edit controls will look:
          at the end. Where editing lives, not that it was taken away. */}
      <p className="dbp-viewonly">
        <Monitor size={14} aria-hidden="true" />
        <span>View only on a phone. Metrics are added, edited and arranged from a computer.</span>
      </p>
      {windowOpen ? (
        <WindowSheet
          frame={frame}
          frameKey={frameKey}
          customActive={customActive}
          buckets={buckets}
          livePref={livePref}
          liveRuns={live}
          onPick={onPickFrame}
          onCustom={onCustom}
          onBucket={onBucket}
          onLive={onLive}
          onClose={() => setWindowOpen(false)}
        />
      ) : null}
    </div>
  );
}
